"use client";

import { Link as RouterLink, useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import type { AnchorHTMLAttributes, MouseEvent } from "react";
import { isLinkableHref } from "shared-web/lib/LinkUtils";
import type { ThemeColor } from "src/lib/FrontendTypes";
import { openOrganisationId, usePathname } from "src/lib/Navigation";
import { resolveCurrentScopePath } from "src/lib/PortalPaths";
import styles from "./Link.module.scss";

/** A URL string; internal destinations start with `/`. */
export type LinkHref = string;

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color" | "href" | "target"> {
  href?: LinkHref;
  variant?: "hyperlink" | "bare" | "minimal";
  disabled?: boolean;
  target?: "_blank";
  color?: ThemeColor;
  /** Preload the destination's code and data on hover or focus (default true). */
  prefetch?: boolean;
  /** Replace the current history entry instead of adding one. */
  replace?: boolean;
  /** Scroll to the top after navigating (default true). */
  scroll?: boolean;
  /** Called once the router starts navigating to `href` (after any navigation guard accepts). */
  onNavigated?: () => void;
}

const isInternalHref = (href: string) => href.startsWith("/") && !href.startsWith("//");

/**
 * Files the Worker serves at a path (Markdown exports, `openapi.yaml`, `llms.txt`, `sitemap.xml`):
 * the router has no page for them, so they load as documents. App routes never contain a dot.
 */
const isFileHref = (href: string) =>
  /\.[a-z0-9]+$/i.test(new URL(href, "http://localhost").pathname);

const isPlainLeftClick = (event: MouseEvent<HTMLAnchorElement>) =>
  event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;

const splitHref = (href: string) => {
  const url = new URL(href, "http://localhost");
  return { pathname: url.pathname, search: url.search, hash: url.hash.replace(/^#/, "") };
};

/**
 * The app's link. Internal paths navigate with TanStack Router (client-side, subject to navigation
 * guards) and preload the destination on hover or focus; external URLs, files the Worker serves,
 * `mailto:` and in-page `#hash` links render a plain anchor. Without `href` it
 * renders a button styled as a link; `disabled`, and an href with a scheme that can't be followed
 * safely (`javascript:`, `data:`), render inert text.
 */
const Link = (props: LinkProps) => {
  const {
    href: rawHref,
    color,
    disabled,
    target,
    variant,
    className,
    children,
    onNavigated,
    prefetch,
    replace,
    scroll,
    onClick,
    ...otherProps
  } = props;
  const router = useRouter();
  // Links to the current project's or organisation's pages resolve against this page's scope.
  const currentPathname = usePathname();
  const href =
    rawHref && resolveCurrentScopePath(rawHref, currentPathname, openOrganisationId(router));

  const sharedProps = {
    "data-link": true,
    // Its own attribute: components that render through Link (menu items, link buttons) set
    // `data-color` for their own styles, which must not pick up the link colours.
    "data-link-color": color,
    "aria-disabled": disabled,
    className: clsx(
      styles.link,
      variant === "hyperlink" && styles.hyperlink,
      variant === "minimal" && styles.minimal,
      className,
    ),
  };

  if (!href) {
    return (
      <button
        type="button"
        disabled={disabled}
        {...sharedProps}
        {...(otherProps as object)}
        onClick={onClick as never}
      >
        {children}
      </button>
    );
  }

  if (disabled || !isLinkableHref(href)) {
    return (
      <span {...sharedProps} {...(otherProps as object)}>
        {children}
      </span>
    );
  }

  if (!isInternalHref(href) || isFileHref(href)) {
    return (
      <a
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        onClick={onClick}
        {...sharedProps}
        {...otherProps}
      >
        {children}
      </a>
    );
  }

  const { pathname, search, hash } = splitHref(href);

  return (
    <RouterLink
      to={pathname as never}
      search={router.options.parseSearch(search) as never}
      hash={hash || undefined}
      target={target}
      replace={replace}
      resetScroll={scroll}
      preload={prefetch === false ? false : "intent"}
      // TanStack marks a link active (and `aria-current="page"`) when its path is a prefix of the
      // current one, so a link to /docs claimed to be every docs page (and DocsSidebar scrolled to
      // it). Only the page itself is current, not links to its sections.
      activeOptions={{ exact: true, includeHash: true }}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (!onNavigated || event.defaultPrevented || target || !isPlainLeftClick(event)) return;
        const destination = router.buildLocation({ href } as never).href;
        const unsubscribe = router.subscribe("onBeforeNavigate", (navigation) => {
          unsubscribe();
          if (navigation.toLocation.href === destination) onNavigated();
        });
        // A guard that rejects the navigation leaves no event behind; stop listening.
        setTimeout(unsubscribe, 30_000);
      }}
      {...sharedProps}
      {...(otherProps as object)}
    >
      {children}
    </RouterLink>
  );
};

export default Link;
